"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Mail, MessageSquare, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { searchStudents, StudentSearchResult } from "@/features/workspaces/api/searchStudents";
import {
  createInvitation,
  CreateInvitationRequest,
} from "@/features/workspaces/api/createInvitation";
// Simple email validation function
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(email);
};

const emailInviteSchema = z.object({
  email: z.string().optional(), // Email is now optional since we're validating user_ids
  description: z.string().max(500, "Description must be 500 characters or less").optional(),
});

export type EmailInviteFormValues = z.infer<typeof emailInviteSchema>;

interface EmailInviteTabProps {
  onSubmit: (data: { students: StudentSearchResult[]; description?: string }) => void;
  submitting: boolean;
  onStudentsChange?: (students: StudentSearchResult[]) => void;
}

export function EmailInviteTab({ onSubmit, submitting, onStudentsChange }: EmailInviteTabProps) {
  const { t } = useTranslation(["workspaces", "common"]);
  const [selectedStudents, setSelectedStudents] = useState<StudentSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const form = useForm<EmailInviteFormValues>({
    resolver: zodResolver(emailInviteSchema),
    defaultValues: {
      email: "",
      description: "",
    },
  });

  // Call onStudentsChange when selectedStudents changes
  useEffect(() => {
    onStudentsChange?.(selectedStudents);
  }, [selectedStudents, onStudentsChange]);

  const handleSearch = useCallback(
    async (email: string) => {
      if (!email || !isValidEmail(email)) {
        setSearchError(null);

        return;
      }

      setSearching(true);
      setSearchError(null);

      try {
        const response = await searchStudents(email);

        if (response.data.length > 0) {
          const student = response.data[0];

          // Check if student is already in the list
          if (!selectedStudents.find((s) => s.id === student.id)) {
            setSelectedStudents((prev) => [...prev, student]);
            form.setValue("email", ""); // Clear the input after successful search
            setValidationError(null); // Clear validation error when students are added
          } else {
            setSearchError("Student already added to the list");
          }
        } else {
          setSearchError(t("workspaces:studentNotFound"));
        }
      } catch (error) {
        setSearchError(t("workspaces:searchError"));
      } finally {
        setSearching(false);
      }
    },
    [t, selectedStudents, form],
  );

  const handleEmailBlur = useCallback(() => {
    const email = form.getValues("email");

    if (email) {
      handleSearch(email);
    }
  }, [form, handleSearch]);

  const handleSearchClick = useCallback(() => {
    const email = form.getValues("email");

    if (email) {
      handleSearch(email);
    }
  }, [form, handleSearch]);

  const handleSubmit = async (data: EmailInviteFormValues) => {
    // Validate that we have at least one student selected
    if (selectedStudents.length === 0) {
      setValidationError("At least one student must be selected");

      return;
    }

    setValidationError(null);

    const submitData: CreateInvitationRequest = {
      type: "EMAIL",
      description: data.description,
      user_ids: selectedStudents.map((s) => s.id),
    };

    try {
      await createInvitation(submitData);
      onSubmit({
        students: selectedStudents, // Pass the full student data
        description: data.description,
      });
    } catch (error) {
      console.error("Error creating invitation:", error);
      // Error handling will be done by the parent component
    }
  };

  const canSubmit = selectedStudents.length > 0 && !submitting;

  return (
    <Card className="border-opacity-50 relative mx-auto h-full w-full overflow-hidden border-none bg-transparent p-2">
      <CardHeader className="space-y-1 ps-0 pt-0 pb-4">
        <CardTitle className="font-bold max-sm:text-sm">
          {t("workspaces:sendEmailInvitation")}
        </CardTitle>
        <CardDescription className="">
          {t("workspaces:sendEmailInvitationDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 px-0">
        <Form {...form}>
          <motion.form
            className="space-y-3 max-sm:space-y-2"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            {/* Required Field: Email Address */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2 text-sm max-sm:text-xs">
                    <Mail className="h-4 w-4 max-sm:h-3 max-sm:w-3" />
                    {t("workspaces:emailAddress")}
                    <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        className="focus:ring-primary/20 h-10 pr-12 transition-all duration-200 focus:ring-2 max-sm:h-9"
                        placeholder={t("workspaces:emailAddressPlaceholder")}
                        type="email"
                        {...field}
                        onBlur={handleEmailBlur}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearchClick();
                          }
                        }}
                      />
                      <Button
                        className="absolute top-1/2 right-1 h-8 w-8 -translate-y-1/2 p-0"
                        disabled={searching}
                        size="sm"
                        type="button"
                        variant="ghost"
                        onClick={handleSearchClick}
                      >
                        {searching ? (
                          <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        ) : (
                          <Search className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormDescription className="text-xs">
                    {t("workspaces:emailAddressHelp")}
                  </FormDescription>
                  <FormMessage />
                  {validationError && (
                    <motion.div
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-950"
                      initial={{ opacity: 0, y: -10 }}
                    >
                      <p className="text-xs text-red-600 dark:text-red-400">{validationError}</p>
                    </motion.div>
                  )}
                </FormItem>
              )}
            />

            {/* Search Error */}
            {searchError && (
              <motion.div animate={{ opacity: 1, y: 0 }} initial={{ opacity: 0, y: -10 }}>
                <p className="text-sm text-red-500">{searchError}</p>
              </motion.div>
            )}

            {/* Optional Field: Message */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2 text-sm max-sm:text-xs">
                    <MessageSquare className="h-4 w-4 max-sm:h-3 max-sm:w-3" />
                    {t("workspaces:message")}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      className="focus:ring-primary/20 min-h-20 resize-none transition-all duration-200 focus:ring-2 max-sm:min-h-[50px]"
                      maxLength={500}
                      placeholder={t("workspaces:workspaceDescriptionPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className="flex justify-end text-xs">
                    <span
                      className={`font-mono ${field.value && field.value.length > 400 ? "text-amber-500" : ""}`}
                    >
                      {field?.value?.length ?? 0}/500
                    </span>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              className="h-10 w-full gap-2 max-sm:h-12"
              disabled={!canSubmit}
              size="lg"
              type="submit"
              variant={"foreground"}
            >
              {submitting ? t("common:loading") : t("workspaces:sendEmailInvitation")}
            </Button>
          </motion.form>
        </Form>
      </CardContent>
    </Card>
  );
}
